import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import SearchInput from "./components/SearchInput";
import Pagination from "./components/Pagination";
import Toast from "./components/Toast";
import DataTable from "./components/DataTable";
import BlogModal from "./components/BlogModal";
import ViewContentModal from "./components/ViewContentModal";
import KeywordsModal from "./components/KeywordsModal";
import MediaGalleryModal from "./components/MediaGalleryModal";
import SocialLinksModal from "./components/SocialLinksModal";
import DeleteModal from "pages/inspiration/components/DeleteModal";

const apiUrl = import.meta.env.VITE_API_URL;
const SOCIAL_LINK_KEYS = ["facebook", "instagram", "linkedin", "website", "twitter"];

const normalizeSocialLinks = (blog) => {
  return SOCIAL_LINK_KEYS.reduce((acc, key) => {
    const value = blog?.socialLinks?.[key] ?? blog?.[key] ?? "";
    if (typeof value === "string") {
      acc[key] = value;
    } else if (value != null) {
      acc[key] = String(value);
    } else {
      acc[key] = "";
    }
    return acc;
  }, {});
};

const Blogs = () => {
  const defaultSort = { key: "createdAt", direction: "desc" };
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const [blogModalOpen, setBlogModalOpen] = useState(false);
  const [viewContentModalOpen, setViewContentModalOpen] = useState(false);
  const [keywordsModalOpen, setKeywordsModalOpen] = useState(false);
  const [mediaGalleryOpen, setMediaGalleryOpen] = useState(false);
  const [socialLinksModalOpen, setSocialLinksModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blog, setBlog] = useState(null);
  const [addAndEditLoader, setAddAndEditLoader] = useState(false);

  const searchTriggered = useRef(false);
  const searchClicked = useRef(false);
  const debounced = useRef(null);
  //  const theme = useTheme();


  const columns = [
    { key: "sno", label: "Sno", sortable: false },
    { key: "images", label: "Image/Video", sortable: false },
    { key: "title", label: "Title", sortable: true },
    { key: "subTitle", label: "Subtitle", sortable: false },
    { key: "category", label: "Category", sortable: false },
    { key: "titlePosition", label: "Title Position", sortable: false },
    { key: "titleColor", label: "Title Color", sortable: false },
    { key: "categoryColor", label: "Category Color", sortable: false },
    { key: "shortDescription", label: "Short Description", sortable: false },
    { key: "description", label: "Description", sortable: false },
    { key: "keywords", label: "Keywords", sortable: false },
    { key: "socialLinks", label: "Social Links", sortable: false },
    { key: "createdAt", label: "Created At", sortable: false },
    { key: "isActive", label: "Active", sortable: false },
    { key: "actions", label: "Actions", sortable: false },
  ];

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        page: currentPage,
        limit: itemsPerPage,
      });
      const res = await fetch(`${apiUrl}/api/admin-user/blogs/list?${params.toString()}`);
      const json = await res.json();
      if (res.ok) {
        const normalizedData = (json.data || []).map((item) => ({
          ...item,
          socialLinks: normalizeSocialLinks(item),
        }));
        setData(normalizedData);
        setTotalPages(json.totalPages);
      } else {
        throw new Error(json.error || "Failed to fetch data.");
      }
      setError(null);
    } catch (err) {
      setError("Failed to fetch data.");
      setToast({ show: true, message: "Failed to fetch data.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      const { key, direction } = sortConfig;
      const aVal = a[key]?.toString().toLowerCase() ?? "";
      const bVal = b[key]?.toString().toLowerCase() ?? "";
      if (aVal < bVal) return direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return direction === "ascending" ? 1 : -1;
      return 0;
    });
    setData(sorted);
  }, [sortConfig]);

  useEffect(() => {
    if (!searchTriggered.current) fetchData();
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (searchTriggered.current) {
      searchTriggered.current = false;
      fetchData();
    }
  }, [searchTriggered.current]);

  useEffect(() => {
    if (searchClicked.current) {
      debounced.current = setTimeout(() => {
        setCurrentPage(1);
        setSortConfig(defaultSort);
        setItemsPerPage(10);
        searchTriggered.current = true;
      }, 1000);
    }
    return () => clearTimeout(debounced.current);
  }, [searchQuery]);

  const requestSort = useCallback(
    (key) => {
      let direction = "ascending";
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleOpenBlogModal = (blog = null) => {
    setSelectedBlog(blog);
    setBlogModalOpen(true);
  };

  const handleOpenViewContentModal = (blog) => {
    setSelectedBlog(blog);
    setViewContentModalOpen(true);
  };

  const handleOpenKeywordsModal = (blog) => {
    setSelectedBlog(blog);
    setKeywordsModalOpen(true);
  };

  const handleOpenMediaGallery = (blog) => {
    setSelectedBlog(blog);
    setMediaGalleryOpen(true);
  };

  const handleOpenSocialLinksModal = (blog) => {
    setSelectedBlog(blog);
    setSocialLinksModalOpen(true);
  };

  const handleDeleteBlog = async (id) => {
    try {
      const res = await fetch(`${apiUrl}/api/admin-user/blogs?id=${id}`, { method: "DELETE" });
      if (res.ok) {
        setShowDeleteModal(false);
        setToast({ show: true, message: "Blog deleted successfully", type: "success" });
        const temp = data.filter((item) => item?._id != id);
        setData(temp);
      } else throw new Error("Delete failed");
    } catch (err) {
      setToast({ show: true, message: "Failed to delete blog", type: "error" });
    }
  };

  const handleSwitch = async (item, isActive) => {
    try {
      const formData = new FormData();
      formData.append("title", item.title);
      formData.append("shortDescription", item.shortDescription);
      formData.append("description", item.description);
      formData.append("subTitle", item.subTitle);
      formData.append("category", item.category);
      formData.append("titlePosition", item.titlePosition);
      formData.append("titleColor", item.titleColor || "#000000");
      formData.append("categoryColor", item.categoryColor || "#000000");
      formData.append("keywords", JSON.stringify(item.keywords));
      formData.append("socialLinks", JSON.stringify(item.socialLinks));
      formData.append("isActive", isActive);
      const res = await fetch(`${apiUrl}/api/admin-user/blogs?id=${item?._id}`, { method: "PUT", body: formData });
      if (res.ok) {
        setToast({ show: true, message: "Blog updated successfully", type: "success" });
        const temp = data.map((obj) => {
          if (item._id == obj._id) {
            return {
              ...obj,
              isActive: isActive
            }
          } else {
            return obj;
          }
        })
        setData(temp);
      } else {
        throw new Error("update blog failed");
      }
    } catch (err) {
      setToast({ show: true, message: "Failed to update blog", type: "error" });
    }
  }

  const handleTableAction = (blog, action) => {
    if (action === "edit") handleOpenBlogModal(blog);
    else if (action === "delete") {
      setShowDeleteModal(true);
      setBlog(blog);
    }
    else if (action === "viewKeywords") {
      handleOpenKeywordsModal(blog);
    }
    else if (action === "viewMedia") {
      handleOpenMediaGallery(blog);
    }
    else if (action === "viewSocialLinks") {
      handleOpenSocialLinksModal(blog);
    }
    else {
      handleOpenViewContentModal(blog);
    }
  };

  return (
    <div className="overflow-x-hidden">
      <div className="flex bg-background">
        <Sidebar
          variant="with-categories"
          onToggle={(collapsed) => setIsCollapsed(collapsed)}
        />
        <div
          className={`w-full transition-all duration-300 ${isCollapsed ? "lg:pl-16" : "lg:pl-64"
            }`}
        >
          <Header isCollapsed={isCollapsed} variant="default" />
          <main className="overflow-y-auto p-4 md:p-6 bg-background">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Blog Management</h1>
              </div>
              <div>
                <button
                  onClick={() => handleOpenBlogModal()}
                  className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
                >
                  Add Blog
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b w-full border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <SearchInput
                  onClick={() => (searchClicked.current = true)}
                  onBlur={() => (searchClicked.current = false)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClear={() => {
                    setSearchQuery("");
                    searchClicked.current = true;
                  }}
                  placeholder="Search blogs..."
                />
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Show:</span>
                  <select
                    className="form-select rounded-md border-gray-300 shadow-sm text-sm"
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    aria-label="Items per page"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                error={error}
                sortConfig={sortConfig}
                onSort={requestSort}
                onRetry={fetchData}
                handleSwitch={handleSwitch}
                emptyMessage="No blogs found."
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onAction={handleTableAction}
              />

              {!isLoading && !error && data?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalItems={data.length}
                  itemsPerPage={itemsPerPage}
                  showingFrom={(currentPage - 1) * itemsPerPage + 1}
                  showingTo={Math.min(currentPage * itemsPerPage, data.length)}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {blogModalOpen && (
        <BlogModal
          addAndEditLoader={addAndEditLoader}
          setAddAndEditLoader={setAddAndEditLoader}
          blog={selectedBlog}
          onClose={() => setBlogModalOpen(false)}
          onSaved={fetchData}
        />
      )}

      {viewContentModalOpen && (
        <ViewContentModal
          blog={selectedBlog}
          onClose={() => setViewContentModalOpen(false)}
        />
      )}
      {keywordsModalOpen && (
        <KeywordsModal
          blog={selectedBlog}
          onClose={() => setKeywordsModalOpen(false)}
        />
      )}
      {mediaGalleryOpen && (
        <MediaGalleryModal
          mediaUrls={selectedBlog?.images || []}
          onClose={() => setMediaGalleryOpen(false)}
          initialIndex={0}
        />
      )}
      {socialLinksModalOpen && (
        <SocialLinksModal
          blog={selectedBlog}
          onClose={() => setSocialLinksModalOpen(false)}
        />
      )}
      {showDeleteModal &&
        <DeleteModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => handleDeleteBlog(blog?._id)}
        />
      }
    </div>
  );
};

export default Blogs;